import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { getCurrentSession, refreshSession } from "@/features/auth/api";
import { loadAccountContextForCurrentSession } from "@/features/auth/hooks/useAuthMutations";
import type { CurrentSessionResponse } from "@/features/auth/types";
import { getStoredRefreshToken, useAuthStore } from "@/lib/auth/authStore";
import { queryClient } from "@/lib/query/queryClient";
import type { NormalizedApiError } from "@/types/api";

interface SessionBootstrapResult {
  session: CurrentSessionResponse;
  accessToken: string;
}

/**
 * Rehidraton sesionin pas refresh-it te faqes duke provuar tokenet ne backend.
 *
 * Refresh token mbetet perkohesisht ne `localStorage` derisa backend-i te kaloje
 * ne HttpOnly cookie. Te dhenat e perdoruesit nuk sajohen ne klient; `/auth/me`
 * verifikon JWT-in dhe kthen identitetin minimal.
 */
export const useSessionBootstrap = (): void => {
  const accessToken = useAuthStore((state) => state.accessToken);
  const setTokens = useAuthStore((state) => state.setTokens);
  const setUser = useAuthStore((state) => state.setUser);
  const setActiveRoles = useAuthStore((state) => state.setActiveRoles);
  const setAccountContext = useAuthStore((state) => state.setAccountContext);
  const setBootstrapState = useAuthStore((state) => state.setBootstrapState);
  const logoutLocal = useAuthStore((state) => state.logoutLocal);
  const refreshToken = getStoredRefreshToken();

  const query = useQuery({
    queryKey: ["auth", "bootstrap", Boolean(refreshToken), Boolean(accessToken)],
    enabled: Boolean(refreshToken) || Boolean(accessToken),
    retry: false,
    queryFn: async (): Promise<SessionBootstrapResult> => {
      if (!accessToken) {
        if (!refreshToken) {
          throw new Error("Refresh token mungon.");
        }

        const refreshResponse = await refreshSession(refreshToken);
        const tokens = refreshResponse.tokens;

        if (!tokens?.access_token || !tokens?.refresh_token) {
          throw new Error("Refresh response nuk përmban token valid.");
        }

        setTokens({
          accessToken: tokens.access_token,
          refreshToken: tokens.refresh_token,
        });
      }

      const session = await getCurrentSession();
      await loadAccountContextForCurrentSession();
      return {
        session,
        accessToken: useAuthStore.getState().accessToken ?? "",
      };
    },
  });

  useEffect(() => {
    if (!refreshToken && !accessToken) {
      setBootstrapState({ isBootstrapping: false, hasBootstrapped: true });
    }
  }, [accessToken, refreshToken, setBootstrapState]);

  useEffect(() => {
    if (!query.data) {
      return;
    }

    const { session } = query.data;
    setUser({
      id: session.account_id,
      accountId: session.account_id,
      systemRole: session.system_role,
      profileRole: session.profile_role,
      businessRole: session.business_role,
      verified: session.verified,
      permissions: session.permissions,
      patientId: session.patient_id,
      clinicianId: session.clinician_id,
      isImpersonating: Boolean(session.is_impersonating),
      impersonatedBy: session.impersonated_by ?? null,
      // G4: ruaj email + fullName per rehydration te UI-t pas refresh-it
      email: session.email ?? undefined,
      fullName: session.full_name ?? undefined,
      avatarUrl: session.avatar_url ?? null,
    });
    setActiveRoles({
      systemRole: session.system_role,
      profileRole: session.profile_role,
      businessRole: session.business_role,
    });
    setAccountContext({
      activeContext: useAuthStore.getState().activeOrganizationContext,
    });
    setBootstrapState({ isBootstrapping: false, hasBootstrapped: true });
  }, [query.data, setAccountContext, setActiveRoles, setBootstrapState, setUser]);

  useEffect(() => {
    if (!query.isError) {
      return;
    }

    if (!isAuthBootstrapFailure(query.error)) {
      setBootstrapState({ isBootstrapping: false, hasBootstrapped: true });
      if (import.meta.env.DEV) {
        console.warn("Auth bootstrap failed without clearing session.", query.error);
      }
      return;
    }

    queryClient.clear();
    logoutLocal();
  }, [logoutLocal, query.error, query.isError, setBootstrapState]);
};

const isAuthBootstrapFailure = (error: unknown): boolean => {
  if (isNormalizedApiError(error)) {
    return error.status === 401 || error.status === 403;
  }

  return error instanceof Error && error.message === "Refresh token mungon.";
};

const isNormalizedApiError = (error: unknown): error is NormalizedApiError => {
  return typeof error === "object" && error !== null && "status" in error && "kind" in error;
};
